"use client";

import { useState, RefObject, useEffect } from 'react';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import { 
  getForcedScrollValue, 
  getScrollOverrideEnabled, 
  getSectionSpecificThresholds 
} from './globalState'; // Import global state getters

type ScrollPosition = 'above' | 'visible' | 'below' | 'outside';

interface ScrollVisibilityOptions {
  animationThresholds?: number[];
  useExtraPosition?: boolean;
  sectionName?: string; // Add section name option
}

export const useScrollVisibility = ({
  animationThresholds = [0, 0.5],
  useExtraPosition = false,
  sectionName = 'main' // Default to main section
}: ScrollVisibilityOptions = {}) => {
  const [position, setPosition] = useState<ScrollPosition>('outside');
  
  // Get custom thresholds for this specific section
  const customThresholds = (typeof window !== 'undefined') 
    ? getSectionSpecificThresholds(sectionName)
    : null;
  
  // Use custom thresholds if available, otherwise use the default ones
  const effectiveThresholds = customThresholds || animationThresholds;
  // Convert to string for use in dependency arrays (to detect changes)
  const thresholdsKey = JSON.stringify(effectiveThresholds) + sectionName;
  
  const { scrollYProgress } = useScroll({
    offset: ["start start", "end start"] as const
  });

  // Function to calculate position based on effective scroll value
  const calculatePosition = (effectiveScroll: number): ScrollPosition => {
    // For multi-point animations, the threshold array could be longer
    // Let's detect if we're dealing with a multi-point component
    const isMultiPoint = effectiveThresholds.length > 2;
    let delta: number;
    
    // Different logic for multi-point vs single-point
    if (isMultiPoint) {
      // For multi-point, we'll check each pair of thresholds separately
      // First determine which section we're in (if any)
      let inSection = false;
      
      for (let i = 0; i < effectiveThresholds.length - 1; i += 2) {
        const sectionStart = effectiveThresholds[i];
        const sectionEnd = effectiveThresholds[i + 1];
        
        // Check if we're in this section
        if (effectiveScroll >= sectionStart && effectiveScroll <= sectionEnd) {
          inSection = true;
          //console.log(`[useScrollVisibility][${sectionName}] In section ${i/2 + 1}: ${sectionStart} - ${sectionEnd}, scroll: ${effectiveScroll}`);
          break;
        }
      }
      
      // Debug output for multi-point
      //console.log(`[useScrollVisibility][${sectionName}] Multi-point calculation:`, {
      //   effectiveScroll,
      //   thresholds: effectiveThresholds,
      //   isMultiPoint,
      //   inSection
      // });
      
      // If we're not in any section, determine if we're above or below
      if (!inSection) {
        if (effectiveScroll < effectiveThresholds[0]) {
          //console.log(`[useScrollVisibility][${sectionName}] Position: above (multi-point)`);
          return 'above';
        } else if (effectiveScroll > effectiveThresholds[effectiveThresholds.length - 1]) {
          //console.log(`[useScrollVisibility][${sectionName}] Position: below (multi-point)`);
          return 'below';
        } else {
          // We're between sections
          //console.log(`[useScrollVisibility][${sectionName}] Position: between sections (multi-point)`);
          return 'outside';
        }
      } else {
        //console.log(`[useScrollVisibility][${sectionName}] Position: visible (multi-point)`);
        return 'visible';
      }
    }
    
    // Original single-point logic
    delta = effectiveThresholds[1] - effectiveThresholds[0];
    
    // Debug output for single-point
    //console.log(`[useScrollVisibility][${sectionName}] Single-point calculation:`, {
    //   effectiveScroll,
    //   thresholds: effectiveThresholds,
    //   delta,
    //   useExtraPosition,
    //   outsideThresholdLow: effectiveThresholds[0]-delta*1.1,
    //   outsideThresholdHigh: effectiveThresholds[1]+delta*1.1
    // });
    
    if (useExtraPosition && (effectiveScroll < effectiveThresholds[0]-delta*1.1 || effectiveScroll > effectiveThresholds[1]+delta*1.1 )) {
      //console.log(`[useScrollVisibility][${sectionName}] Position: outside`);
      return 'outside';
    } else if (effectiveScroll < effectiveThresholds[0]) {
      //console.log(`[useScrollVisibility][${sectionName}] Position: above`);
      return 'above';
    } else if (effectiveScroll > effectiveThresholds[effectiveThresholds.length - 1]) {
      //console.log(`[useScrollVisibility][${sectionName}] Position: below`);
      return 'below';
    } else {
      //console.log(`[useScrollVisibility][${sectionName}] Position: visible`);
      return 'visible';
    }
  };

  // Effect to recalculate position when thresholds change
  useEffect(() => {
    const overrideEnabled = getScrollOverrideEnabled();
    const forcedValue = getForcedScrollValue();
    let currentScroll: number;

    if (overrideEnabled && forcedValue !== null) {
      //console.log(`[useScrollVisibility][${sectionName}] Thresholds changed, using FORCED scroll value:`, forcedValue);
      currentScroll = forcedValue;
    } else {
      currentScroll = scrollYProgress.get();
    }
    
    //console.log(`[useScrollVisibility][${sectionName}] Recalculating position after threshold change:`, thresholdsKey);
    setPosition(calculatePosition(currentScroll));
  }, [thresholdsKey]);
  
  // Effect to recalculate when forced value changes
  useEffect(() => {
    // Direct event listener for forced scroll value changes
    const handleForcedScrollValueChange = (e: CustomEvent) => {
      const forcedValue = e.detail as number | null;
      if (forcedValue !== null) {
        //console.log(`[useScrollVisibility][${sectionName}] Forced value changed (event):`, forcedValue);
        setPosition(calculatePosition(forcedValue));
      }
    };
    
    // Direct event listener for threshold changes 
    const handleThresholdsChange = (e: CustomEvent) => {
      const thresholds = e.detail as number[] | null;
      const overrideEnabled = getScrollOverrideEnabled();
      const forcedValue = getForcedScrollValue();
      
      if (thresholds && overrideEnabled && forcedValue !== null) {
        //console.log(`[useScrollVisibility][${sectionName}] Thresholds changed (event):`, thresholds);
        setPosition(calculatePosition(forcedValue));
      }
    };
    
    // Add event listeners
    window.addEventListener('forced-scroll-value-change', handleForcedScrollValueChange as EventListener);
    window.addEventListener('custom-thresholds-change', handleThresholdsChange as EventListener);

    // Fallback polling method for older browsers or as backup
    const checkForcedValueChanges = () => {
      const overrideEnabled = getScrollOverrideEnabled();
      const forcedValue = getForcedScrollValue();
      
      if (overrideEnabled && forcedValue !== null) {
        //console.log(`[useScrollVisibility][${sectionName}] Forced value changed in interval check:`, forcedValue);
        setPosition(calculatePosition(forcedValue));
      }
    };
    
    // Check every 250ms for forced value changes (as backup)
    const interval = setInterval(checkForcedValueChanges, 250);
    
    // Clean up
    return () => {
      clearInterval(interval);
      window.removeEventListener('forced-scroll-value-change', handleForcedScrollValueChange as EventListener);
      window.removeEventListener('custom-thresholds-change', handleThresholdsChange as EventListener);
    }
  }, [effectiveThresholds, useExtraPosition, sectionName]);

  // Initial position check on mount
  useEffect(() => {
    const overrideEnabled = getScrollOverrideEnabled();
    const forcedValue = getForcedScrollValue();
    let currentScroll: number;

    if (overrideEnabled && forcedValue !== null) {
      //console.log(`[useScrollVisibility][${sectionName}] Using FORCED scroll value (initial):`, forcedValue);
      currentScroll = forcedValue;
    } else {
      currentScroll = scrollYProgress.get();
    }
    
    setPosition(calculatePosition(currentScroll));

  }, []); // Only run on mount

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const overrideEnabled = getScrollOverrideEnabled();
    const forcedValue = getForcedScrollValue();
    let effectiveScroll: number;

    if (overrideEnabled && forcedValue !== null) {
      //console.log(`[useScrollVisibility][${sectionName}] Using FORCED scroll value:`, forcedValue);
      effectiveScroll = forcedValue;
    } else {
      effectiveScroll = latest;
    }

    const newPosition = calculatePosition(effectiveScroll);
    if (newPosition !== position) {
      //console.log(`[useScrollVisibility][${sectionName}] Position changed:`, newPosition, 'Scroll:', effectiveScroll, 'Thresholds:', effectiveThresholds);
      setPosition(newPosition);
    }
  });

  return position;
};